/**
 * Права ролей на стороне интерфейса — чтобы не показывать кнопку, которая
 * заведомо вернёт 403. Источник правды всё равно бэкенд: здесь только то, что
 * он уже проверяет (app/auth/dependencies.py, ALLOWED_DASHBOARD_ROLES).
 */

import type { Me, Role } from '@/api/types'

/** Вход в интерфейс вообще (гейт SPA, routes/_auth.tsx): врач тоже пускается —
 * ему нужен экран приёма (/encounters/new), не дашборд. */
export const APP_ROLES: Role[] = ['doctor', 'head', 'clinic_head', 'admin']

export function canOpenApp(role: Role): boolean {
  return APP_ROLES.includes(role)
}

/** Раздел «Обзор» и то, что было гейтовано им раньше: заведующий отделением,
 * заведующий клиникой и администратор. */
export const DASHBOARD_ROLES: Role[] = ['head', 'clinic_head', 'admin']

export function canOpenDashboard(role: Role): boolean {
  return DASHBOARD_ROLES.includes(role)
}

export function canRecordEncounter(user: Me | undefined): boolean {
  return user?.role === 'doctor'
}

/** Заведующий отделением тоже видит раздел «Пользователи» (своих отделений),
 * но не редактирует. Заведующий клиникой видит и редактирует в рамках своей
 * клиники (см. canManageUsers). */
export function canViewUsers(user: Me | undefined): boolean {
  return user?.role === 'admin' || user?.role === 'head' || user?.role === 'clinic_head'
}

/** Заведующий клиникой управляет пользователями своей клиники — фактически
 * admin, но с областью действия, которую всё равно проверяет бэкенд
 * (app/api/users.py). */
export function canManageUsers(user: Me | undefined): boolean {
  return user?.role === 'admin' || user?.role === 'clinic_head'
}

/** Клиника как сущность (в отличие от отделений внутри неё) — только admin,
 * даже заведующий своей клиникой её не редактирует. */
export function canManageClinics(user: Me | undefined): boolean {
  return user?.role === 'admin'
}

/** Заведующий клиникой управляет отделениями своей клиники (создание/архив/
 * назначение врачей) — область действия проверяет бэкенд (app/api/departments.py). */
export function canManageDepartments(user: Me | undefined): boolean {
  return user?.role === 'admin' || user?.role === 'clinic_head'
}

export function canDeleteEncounters(user: Me | undefined): boolean {
  return user?.role === 'admin'
}

/** Безвозвратное удаление клиента вместе со всеми его приёмами — та же
 * граница, что и у purge приёма (app/api/patients.py:delete_patient). */
export function canDeletePatients(user: Me | undefined): boolean {
  return user?.role === 'admin'
}

export function canRefreshReview(user: Me | undefined): boolean {
  return user?.role === 'admin' || user?.role === 'head' || user?.role === 'clinic_head'
}

/** Фильтр по отделению имеет смысл только тем, кто видит больше одного. */
export function canFilterByDepartment(user: Me | undefined): boolean {
  return user?.role === 'admin' || user?.role === 'clinic_head'
}

/** Демо-приём с примером данных — учебный материал для внедрения, не
 * реальные данные пациентов, поэтому виден только администратору. */
export function canViewDemoEncounter(user: Me | undefined): boolean {
  return user?.role === 'admin'
}
